import { useId, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { ChevronDown, Loader2, Printer } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { SitesCombobox } from '@/components/common/sites-combobox';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { type EmbryoDto } from '@/services/embryo-service';
import {
  useEmbryoInventoryByEmbryoReport,
  useFrozenEmbryoThawAndImplantReport,
} from '@/services/reports';

type EmbryoReportsDropdownProps = {
  selectedEmbryos: EmbryoDto[];
};

const thawAndImplantFormSchema = z.object({
  implantDate: z.date().optional(),
  siteId: z.string().optional(),
});

type ThawAndImplantFormValues = z.infer<typeof thawAndImplantFormSchema>;

const getEmbryoIds = (embryos: EmbryoDto[]) =>
  embryos.map((embryo) => embryo.id).filter((id): id is string => Boolean(id));

const getSharedSiteId = (embryos: EmbryoDto[]) => {
  const siteIds = new Set(
    embryos.map((embryo) => embryo.siteId).filter(Boolean),
  );

  return siteIds.size === 1 ? [...siteIds][0] : undefined;
};

export function EmbryoReportsDropdown({
  selectedEmbryos,
}: EmbryoReportsDropdownProps) {
  const [showThawAndImplantDialog, setShowThawAndImplantDialog] =
    useState(false);
  const implantDateId = useId();
  const siteId = useId();
  const formId = useId();
  const { control, handleSubmit, reset } = useForm<ThawAndImplantFormValues>({
    resolver: zodResolver(thawAndImplantFormSchema),
    defaultValues: {
      implantDate: undefined,
      siteId: undefined,
    },
  });
  const [generateInventory, inventoryReport] =
    useEmbryoInventoryByEmbryoReport();
  const [generateThawAndImplant, thawAndImplantReport] =
    useFrozenEmbryoThawAndImplantReport();

  const embryoIds = getEmbryoIds(selectedEmbryos);
  const isGenerating =
    inventoryReport.isPending || thawAndImplantReport.isPending;
  const isDisabled = embryoIds.length === 0 || isGenerating;

  const openThawAndImplantDialog = () => {
    reset({
      implantDate: undefined,
      siteId: getSharedSiteId(selectedEmbryos),
    });
    setShowThawAndImplantDialog(true);
  };

  const handleGenerateThawAndImplant = async (
    values: ThawAndImplantFormValues,
  ) => {
    setShowThawAndImplantDialog(false);

    try {
      await generateThawAndImplant({
        embryoIds,
        implantDate: values.implantDate
          ? format(values.implantDate, 'yyyy-MM-dd')
          : undefined,
        siteId: values.siteId,
      });
    } catch {
      setShowThawAndImplantDialog(true);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={isDisabled}
          render={
            <Button variant="outline" disabled={isDisabled}>
              {isGenerating ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Printer className="size-3.5" />
              )}
              Reports
              <ChevronDown className="size-3 opacity-60" />
            </Button>
          }
        />
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuItem onClick={() => generateInventory({ embryoIds })}>
            Embryo Inventory by Embryo
          </DropdownMenuItem>
          <DropdownMenuItem onClick={openThawAndImplantDialog}>
            Frozen Embryo Thaw and Implant
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={showThawAndImplantDialog}
        onOpenChange={setShowThawAndImplantDialog}
      >
        <DialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle>Frozen Embryo Thaw and Implant</DialogTitle>
            <DialogDescription>
              Set the values printed on the form for the selected embryos.
            </DialogDescription>
          </DialogHeader>

          <form
            id={formId}
            className="grid gap-4 py-2"
            onSubmit={handleSubmit(handleGenerateThawAndImplant)}
          >
            <div className="grid gap-2">
              <Label htmlFor={implantDateId}>Implant date</Label>
              <Controller
                control={control}
                name="implantDate"
                render={({ field }) => (
                  <DatePicker
                    id={implantDateId}
                    value={field.value}
                    onChange={field.onChange}
                    className="h-8"
                  />
                )}
              />
              <p className="text-muted-foreground text-xs">
                Leave blank to print an empty implant date.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor={siteId}>Site</Label>
              <Controller
                control={control}
                name="siteId"
                render={({ field }) => (
                  <SitesCombobox
                    id={siteId}
                    value={field.value}
                    onChange={field.onChange}
                    className="h-8 w-full"
                  />
                )}
              />
              <p className="text-muted-foreground text-xs">
                Leave blank to print an empty site.
              </p>
            </div>
          </form>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowThawAndImplantDialog(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              disabled={thawAndImplantReport.isPending}
            >
              {thawAndImplantReport.isPending && (
                <Loader2 className="size-3.5 animate-spin" />
              )}
              Generate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
